import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { templatesAPI } from '../services/api'
import type { ContractTemplate } from '../types'

export default function TemplateList() {
  const { t } = useTranslation()
  const [templates, setTemplates] = useState<ContractTemplate[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    templatesAPI.list()
      .then((resp) => setTemplates(resp.data.templates || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const handleDuplicate = async (id: number) => {
    try {
      await templatesAPI.duplicate(id)
      const resp = await templatesAPI.list()
      setTemplates(resp.data.templates || [])
    } catch { alert('Error al duplicar') }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar plantilla?')) return
    try {
      await templatesAPI.delete(id)
      setTemplates((prev) => prev.filter((t) => t.id !== id))
    } catch { alert('Error al eliminar') }
  }

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-files me-2"></i>{t('template.title')}</h1>
        <Link to="/templates/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-1"></i>
          {t('template.new')}
        </Link>
      </div>

      <div className="row g-3">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : templates.length === 0 ? (
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-files display-4 d-block mb-2"></i>
                <p>No hay plantillas</p>
                <Link to="/templates/new" className="btn btn-primary btn-sm">
                  <i className="bi bi-plus-lg"></i> {t('template.new')}
                </Link>
              </div>
            </div>
          </div>
        ) : (
          templates.map((tpl) => (
            <div key={tpl.id} className="col-md-4">
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0">{tpl.name}</h6>
                    <span className="badge bg-secondary">v{tpl.version}</span>
                  </div>
                  <p className="card-text small text-muted mb-2">
                    {tpl.description || 'Sin descripción'}
                  </p>
                  <div className="d-flex gap-1">
                    <span className="badge bg-dark">{tpl.locale?.toUpperCase()}</span>
                    {tpl.sections && <span className="badge bg-dark">{tpl.sections.length} secciones</span>}
                    {tpl.variables && <span className="badge bg-dark">{tpl.variables.length} variables</span>}
                  </div>
                </div>
                <div className="card-footer bg-transparent border-top-0 d-flex gap-2">
                  <Link to={`/templates/${tpl.id}/edit`} className="btn btn-sm btn-outline-primary flex-fill">
                    <i className="bi bi-pencil me-1"></i> {t('common.edit')}
                  </Link>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => handleDuplicate(tpl.id)}>
                    <i className="bi bi-copy"></i>
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(tpl.id)}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
